import { useEffect, useState } from 'preact/hooks';
import { currentScreen, activeGame, gameSetup } from '../../App';
import { sfx } from '../../lib/sfx';
import { PixelModal } from '../ui/PixelModal';

interface SaveSummary {
  id: string;
  universe_id: string;
  genre: string;
  language: string;
  mode: string;
  duration_tier: string;
  booker_arc: string;
  current_act: number;
  turn_count: number;
  status: string;
  created_at: string;
  updated_at: string;
}

type Lang = 'es' | 'en' | 'pt';

const LABELS: Record<string, Record<Lang, string>> = {
  title: { es: 'TUS PARTIDAS', en: 'YOUR GAMES', pt: 'SUAS PARTIDAS' },
  empty: { es: 'No tienes partidas guardadas.', en: 'No saved games.', pt: 'Nenhuma partida salva.' },
  back: { es: 'Volver', en: 'Back', pt: 'Voltar' },
  continue: { es: 'Continuar', en: 'Continue', pt: 'Continuar' },
  delete: { es: 'Eliminar', en: 'Delete', pt: 'Eliminar' },
  newgame: { es: 'Nueva partida', en: 'New game', pt: 'Nova partida' },
  turn: { es: 'Turno', en: 'Turn', pt: 'Turno' },
  loading: { es: 'Cargando...', en: 'Loading...', pt: 'Carregando...' },
  confirm: { es: '¿Eliminar esta partida?', en: 'Delete this game?', pt: 'Eliminar esta partida?' },
};

const DURATION_LABELS: Record<string, string> = {
  quick_play: 'Quick Play',
  aventura: 'Aventura',
  historia: 'Historia',
};

export function SaveLoadScreen() {
  const [saves, setSaves] = useState<SaveSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<SaveSummary | null>(null);
  const lang = (gameSetup.value.language ?? 'es') as Lang;
  const tr = (k: string) => LABELS[k]?.[lang] ?? LABELS[k]?.en ?? k;

  useEffect(() => {
    fetch('/api/game/saves', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        setSaves(data.saves ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  function handleContinue(save: SaveSummary) {
    sfx.success();
    activeGame.value = { saveId: save.id, wsUrl: `/api/game/ws/${save.id}` };
    currentScreen.value = 'game';
  }

  async function handleDelete(save: SaveSummary) {
    sfx.error();
    setPendingDelete(save);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    sfx.click();
    await fetch(`/api/game/saves/${pendingDelete.id}`, { method: 'DELETE', credentials: 'include' });
    setSaves((prev) => prev.filter((s) => s.id !== pendingDelete.id));
    setPendingDelete(null);
  }

  return (
    <div class="center-screen">
      <div class="pixel-border container">
        <h2 class="pixel-title text-yellow mb-3 text-center" style={{ fontSize: '18px' }}>
          {tr('title')}
        </h2>

        {loading && <p class="text-dim text-center">{tr('loading')}</p>}

        {!loading && saves.length === 0 && (
          <div class="text-center mb-3">
            <p class="text-dim mb-2">{tr('empty')}</p>
            <button class="pixel-btn text-green" onClick={() => { sfx.click(); currentScreen.value = 'universe'; }}>
              {tr('newgame')}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
          {saves.map((save) => (
            <div key={save.id} class="pixel-border" style={{ padding: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span class="text-yellow" style={{ fontSize: '18px' }}>{save.universe_id}</span>
                  <span class="text-dim" style={{ fontSize: '14px', marginLeft: '0.5rem' }}>
                    {DURATION_LABELS[save.duration_tier] ?? save.duration_tier}
                  </span>
                  {save.status === 'completed' && <span class="text-green" style={{ fontSize: '14px', marginLeft: '0.5rem' }}>[✓]</span>}
                  <p class="text-dim" style={{ fontSize: '14px' }}>
                    {save.genre} | {save.booker_arc} (Act {save.current_act}) | {tr('turn')} {save.turn_count}
                  </p>
                  <p class="text-dim" style={{ fontSize: '12px' }}>
                    {new Date(save.updated_at + 'Z').toLocaleString()}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <button
                    class="pixel-btn text-green"
                    disabled={save.status === 'completed'}
                    onClick={() => handleContinue(save)}
                    style={{ fontSize: '14px' }}
                  >
                    {tr('continue')}
                  </button>
                  <button
                    class="pixel-btn text-red"
                    onClick={() => handleDelete(save)}
                    style={{ fontSize: '13px', padding: '0.2rem 0.6rem' }}
                  >
                    {tr('delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div class="mt-3" style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button class="pixel-btn" onClick={() => { sfx.click(); currentScreen.value = 'start'; }}>
            {tr('back')}
          </button>
          <button class="pixel-btn text-green" onClick={() => { sfx.click(); currentScreen.value = 'universe'; }}>
            {tr('newgame')}
          </button>
        </div>
      </div>

      {pendingDelete && (
        <PixelModal
          title="ELIMINAR PARTIDA"
          message={tr('confirm')}
          confirmText={tr('delete')}
          cancelText={tr('back')}
          onConfirm={confirmDelete}
          onCancel={() => { sfx.click(); setPendingDelete(null); }}
        />
      )}
    </div>
  );
}
